import React, { useEffect, useState } from 'react'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import _get from 'lodash/get'
import { getBoundActions } from '../../actions/index'
import { connect as connectStaticText } from '../StaticText/connect'
import GaiaLogo, { TYPE_TEAL } from '../GaiaLogo/index'
import PlanCardGrid from '../PlanCardGrid/index'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../Loading/index'
import { HISTORY_METHOD_REPLACE, historyRedirect } from '../../services/navigation/index'
import { getPrimary } from '../../services/languages/index'
import { URL_CART_ACCESS_DENIED, URL_ACCOUNT_CREATION } from '../../services/url/constants'
import { userCheckoutAllowed } from '../../services/checkout/index'
import { get as getConfig } from '../../config/index'
import { shouldShowWinbackCheckout } from '../../services/winback/index'
import { ENTERED_CHECKOUT_GA4_EVENT } from '../../services/event-tracking/index'

import CartSinglePagePayment from './CartSinglePagePayment'
import CartSinglePageWinback from './CartSinglePageWinback'
import { onEditMail, onSummaryChangePlan, WINBACK_STEPS } from './utils'

const MOBILE_BREAKPOINT = 1164

const config = getConfig()
const isSinglePageCheckoutActive = !!_get(config, ['features', 'singlePageCheckout'])

/**
 * Component to handle loading state
 * @param {Object} props
 * @param {Function} props.setAppScrollable - Function to set app scrollable
 * @returns {React.ReactNode}
 */
function CartSinglePageLoading (props) {
  const { setAppScrollable } = props

  useEffect(() => {
    // Disable scroll
    setAppScrollable(false)

    // Enable scroll on unmount
    return () => {
      setAppScrollable()
    }
  }, [setAppScrollable])

  return (
    <div className="cart-single-page__processing">
      <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
    </div>
  )
}

/**
 * Component to handle the cart single page
 * @param {Map} props.app - App state
 * @param {Map} props.auth - Auth state
 * @param {Map} props.user - User state
 * @param {Map} props.adyen - Adyen state
 * @param {Map} props.payment - Payment state
 * @param {Map} props.checkout - Checkout state
 * @param {Map} props.paytrack - Paytrack state
 * @param {Map} props.staticText - Static text state
 * @param {Map} props.userAccount - User account state
 * @param {Object} props.history - History state
 * @param {Function} props.setCheckoutWinbackStep - Function to set checkout winback step
 * @returns {React.ReactNode}
 */
function CartSinglePage (props) {
  const {
    app,
    auth,
    user,
    adyen,
    payment,
    checkout,
    paytrack,
    staticText,
    userAccount,
    history,
    setEventDefault,
    setCheckoutWinbackStep,
    setCheckoutSinglePageStep,
    sendOptimizelyFeatureEvent,
  } = props

  // early return if single page checkout is not active while QAing
  if (!isSinglePageCheckoutActive) {
    return null
  }

  // Checkout data
  const showWinbackFlow = shouldShowWinbackCheckout(auth, user, userAccount, paytrack)
  const activeStep = checkout.getIn(['winback', 'step']) || WINBACK_STEPS.CHOOSE_PLAN
  const isUserEntitled = user.getIn(['data', 'entitled'])
  const isReactivation = checkout.get('reactivation')
  const checkoutAllowed = userCheckoutAllowed({
    auth,
    user,
    userAccount,
    paytrack,
  }, isUserEntitled)

  // Processing states
  const hasProcessedPaytrack = paytrack.get('processing') === false
  const hasProcessedSubscriptions = userAccount.get('billingSubscriptionsProcessing') === false
  const isPaymentDataProcessing = payment.get('paymentDataProcessing')

  // Variables
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const isLoggedIn = !!auth.get('jwt')
  const isMobile = app.getIn(['viewport', 'width']) < MOBILE_BREAKPOINT
  const [renderProcessing, setRenderProcessing] = useState(true)
  const [renderWinback, setRenderWinback] = useState(true)
  const containerSubfix = activeStep === WINBACK_STEPS.CHOOSE_PLAN ? 'choose-plan' : 'payment'
  const stepTitle = String(staticText.getIn(['data', 'step']))
    .replace(/\$\{ totalSteps \}/, Object.keys(WINBACK_STEPS).length)
    .replace(/\$\{ step \}/, activeStep)

  // reset winback step on mount
  useEffect(() => {
    setCheckoutWinbackStep(null)
  }, [])

  // Handle processing checkout state
  useEffect(() => {
    const isCheckoutProcessing = checkout.get('processing') || isPaymentDataProcessing || false
    if (isCheckoutProcessing) {
      setRenderProcessing(true)
    }
  }, [checkout, adyen, isPaymentDataProcessing])

  // Redirect to regular checkout if not enabled
  useEffect(() => {
    if (hasProcessedPaytrack && hasProcessedSubscriptions && showWinbackFlow === false) {
      historyRedirect({
        history,
        url: URL_ACCOUNT_CREATION,
        auth,
        language: userLanguage,
        historyMethod: HISTORY_METHOD_REPLACE,
      })
    }
    if (hasProcessedPaytrack && hasProcessedSubscriptions && showWinbackFlow === true) {
      // GA4 + optimizely event for Entered Checkout screen
      setEventDefault(ENTERED_CHECKOUT_GA4_EVENT)
      sendOptimizelyFeatureEvent({ eventName: 'entered_checkout' })

      // Set Checkout step
      setCheckoutSinglePageStep(1, 2)
    }
  }, [showWinbackFlow, hasProcessedPaytrack, hasProcessedSubscriptions, userAccount])

  // Handle state if the checkout is not allowed
  useEffect(() => {
    if (checkoutAllowed && isLoggedIn) {
      setRenderWinback(true)
    } else {
      historyRedirect({
        history,
        url: URL_CART_ACCESS_DENIED,
        auth,
        language: userLanguage,
        historyMethod: HISTORY_METHOD_REPLACE,
      })
    }
  }, [isReactivation, isLoggedIn, showWinbackFlow, userAccount])

  // Winback flow
  if (renderWinback) {
    return (
      <>
        {renderProcessing && <CartSinglePageLoading {...props} />}
        <CartSinglePageWinback
          setRenderWinback={setRenderWinback}
          setRenderProcessing={setRenderProcessing}
        />
      </>
    )
  }

  return (
    <div className="cart-single-page">
      {renderProcessing && <CartSinglePageLoading {...props} />}
      {!renderWinback && (
        <div className={`cart-single-page__container cart-single-page__container--${containerSubfix}`}>
          <div className="cart-single-page__gaia-logo">
            <GaiaLogo type={TYPE_TEAL} />
          </div>
          <div className="cart-single-page__step-indicator">
              {stepTitle}
            </div>
          {/* Choose Plan */}
          { activeStep === WINBACK_STEPS.CHOOSE_PLAN && (
            <div className="cart-single-page__choose-plan">
              <div className="cart-single-page__choose-plan-title cart-single-page__section-title">
                {staticText.getIn(['data', 'choosePlan'])}
              </div>
              <PlanCardGrid
                history={history}
                sliderMode={true}
                isMobile={isMobile}
                onPlanSelect={() => { setCheckoutWinbackStep(WINBACK_STEPS.SELECT_PAYMENT) }}
              />
            </div>
          )}
          {/* Select Payment */}
          { activeStep === WINBACK_STEPS.SELECT_PAYMENT && (
            <CartSinglePagePayment
              onChangePlan={() => onSummaryChangePlan(props)}
              onEditMail={() => onEditMail(props)}
              setRenderProcessing={setRenderProcessing}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default compose(
  connectStaticText({ storeKey: 'CartSinglePage' }),
  connectRedux(
    (state) => ({
      app: state.app,
      auth: state.auth,
      adyen: state.adyen,
      user: state.user,
      userAccount: state.userAccount,
      checkout: state.checkout,
      resolver: state.resolver,
      paytrack: state.paytrack,
      plans: state.plans,
      velocityCheck: state.velocityCheck,
      inboundTracking: state.inboundTracking,
      payment: state.payment,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setCheckoutSinglePageStep: actions.checkout.setCheckoutSinglePageStep,
        setEventDefault: actions.eventTracking.setEventDefault,
        setAppScrollable: actions.app.setAppScrollable,
        setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
        resetUserEmailAvailability: actions.user.resetUserEmailAvailability,
        setDialogOptions: actions.dialog.setDialogOptions,
        setCheckoutWinbackStep: actions.checkout.setCheckoutWinbackStep,
        sendOptimizelyFeatureEvent: actions.optimizelyFeature.sendOptimizelyFeatureEvent,
      }
    },
  ),
)(CartSinglePage)
